/**
 * Admission consent form builder — renders the inline preview with editable
 * fields and wires it together with the PDF via `withDocumentEditor`.
 */
import { Input } from '@/frontend/components/ui/input'
import { ClinicHeaderPreview } from '@/frontend/features/printing/documents/blocks/clinic-header'
import { DocumentPreview } from '@/frontend/features/printing/documents/blocks/document-preview'
import { OwnerDetailsPreview } from '@/frontend/features/printing/documents/blocks/owner-details'
import { OwnerSignaturePreview } from '@/frontend/features/printing/documents/blocks/owner-signature'
import { PatientDetailsPreview } from '@/frontend/features/printing/documents/blocks/patient-details'
import { VetSignoffPreview } from '@/frontend/features/printing/documents/blocks/vet-signoff'
import { AdmissionConsentPdf } from '@/frontend/features/printing/documents/consent/admission-template.print'
import type {
  AdmissionConsentConfig,
  AdmissionConsentFields,
} from '@/frontend/features/printing/documents/consent/admission-template.types'
import { defaultAdmissionConsentFields } from '@/frontend/features/printing/documents/consent/admission-template.types'
import type {
  DocumentContext,
  DocumentFormProps,
} from '@/frontend/features/printing/documents/types'
import { withDocumentEditor } from '@/frontend/features/printing/documents/with-document-editor'

function AdmissionConsentForm({
  config,
  context,
  value,
  onChange,
}: DocumentFormProps<AdmissionConsentFields> & { config: AdmissionConsentConfig }) {
  const { patient, owner, clinic } = context

  return (
    <DocumentPreview>
      <ClinicHeaderPreview clinic={clinic} />

      <h1 className="mb-4 text-center text-lg font-bold underline">{config.title}</h1>

      <OwnerDetailsPreview owner={owner} />
      <hr className="my-4" />
      <PatientDetailsPreview
        patient={patient}
        weight={context.weight}
        showDob
        showWeight
        showMicrochip
      />
      <hr className="my-4" />

      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <span className="font-semibold">Weight today (kg):</span>
          <Input
            className="w-48"
            value={value.weightToday}
            onChange={(e) => onChange({ ...value, weightToday: e.target.value })}
            placeholder={context.weight ?? undefined}
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="font-semibold">Allergies:</span>
          <Input
            className="w-64"
            value={value.allergies}
            onChange={(e) => onChange({ ...value, allergies: e.target.value })}
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="font-semibold">Current Medications:</span>
          <Input
            className="w-64"
            value={value.currentMedications}
            onChange={(e) => onChange({ ...value, currentMedications: e.target.value })}
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="font-semibold">PROCEDURE:</span>
          <Input
            className="w-64"
            value={value.procedure}
            onChange={(e) => onChange({ ...value, procedure: e.target.value })}
          />
        </div>
      </div>

      <p className="mt-6 font-semibold">
        I consent to, and understand the following relating to my pet:
      </p>
      <div className="mt-3 space-y-3">
        {config.clauses.map((clause) => (
          <div key={clause.slice(0, 40)} className="flex items-start gap-2">
            <div className="mt-0.5 h-4 w-4 shrink-0 rounded-sm border border-muted-foreground" />
            <span>{clause}</span>
          </div>
        ))}
      </div>

      <OwnerSignaturePreview owner={owner} label="Owner or Authorised Agent" prefillName={false} />

      <hr className="my-4" />

      <VetSignoffPreview
        vetName={value.vetName}
        onChange={(v) => onChange({ ...value, vetName: v.vetName })}
        label="ADMITTED BY"
      />
    </DocumentPreview>
  )
}

/** Build an admission consent form from a config of clauses and metadata. */
export function buildAdmissionConsentForm(config: AdmissionConsentConfig) {
  return withDocumentEditor({
    id: config.id,
    label: config.label,
    clinic: config.clinic,
    defaultFields: { ...defaultAdmissionConsentFields },
    Preview: (props: DocumentFormProps<AdmissionConsentFields>) => (
      <AdmissionConsentForm {...props} config={config} />
    ),
    Pdf: ({ context, fields }: { context: DocumentContext; fields: AdmissionConsentFields }) => (
      <AdmissionConsentPdf config={config} context={context} fields={fields} />
    ),
  })
}
